import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  TextField,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Typography,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material'
import { Info } from '@mui/icons-material'
import { sentenceCase } from '@/utils'

import { StateSchema } from '@/hooks/form/models'
import keys from '@/utils/keys'
import {
  BonusModeType,
  BonusRolloverAmountType,
  BonusType,
  BonusWageringOrderType,
  CurrencyCodeEnum,
  BonusTriggerType,
} from '@/enums'

import NumberFormatCustom from '@/views/components/input-number-format/index.component'
import CurrenciesTableComponent from '@/views/components/currencies-table/currencies-table.components'
import { authSelector } from '@/state/auth/slices/index.slices'

type Props = {
  handleOnChange: (name: string, value: any) => void
  state: StateSchema
  bonusType: string
  triggerBy: number
}

const RolloverSettingsComponent = ({
  handleOnChange,
  state,
  bonusType,
  triggerBy,
}: Props) => {
  const { t } = useTranslation()

  const { brand } = useSelector(authSelector)

  const currencyHeadCells = ['baseCurrency', 'rmMinAmount']

  let modeTypes = [
    ...keys(BonusModeType).map((x) => {
      return {
        index: BonusModeType[x],
        value: x,
      }
    }),
  ].sort((a, b) => a.value.localeCompare(b.value))

  // if (bonusType === BonusType.FREE_PLAY) {
  //   modeTypes = modeTypes.filter((mode) => Number(mode.index) === 2)
  // } else if (
  if (
    bonusType === BonusType.BETSOFT_CASINO ||
    triggerBy === BonusTriggerType.BY_BUNDLE ||
    triggerBy === BonusTriggerType.BY_CODE
  ) {
    modeTypes = modeTypes.filter((mode) => Number(mode.index) === 3)
  } else {
    modeTypes = modeTypes.filter((mode) => Number(mode.index) !== 2)
  }

  let rolloverTypes = [
    ...keys(BonusRolloverAmountType)
      .filter((item) => typeof BonusRolloverAmountType[item] !== 'string')
      .map((x) => {
        return {
          index: BonusRolloverAmountType[x],
          value: x,
        }
      }),
  ].sort((a, b) => a.value.localeCompare(b.value))

  if (
    bonusType === BonusType.FREE_SPINS ||
    bonusType === BonusType.BETSOFT_CASINO ||
    triggerBy === BonusTriggerType.BY_BUNDLE ||
    triggerBy === BonusTriggerType.BY_CODE
  ) {
    rolloverTypes = rolloverTypes.filter(
      (rollover) => Number(rollover.index) === 1,
    )
  }

  const wageringOrderOptions = [
    ...keys(BonusWageringOrderType).map((x) => {
      return {
        index: BonusWageringOrderType[x],
        value: x,
      }
    }),
  ].sort((a, b) => a.value.localeCompare(b.value))

  return (
    <>
      <Box maxWidth={400}>
        <Typography fontWeight={700}>{t('bonusModeLabel')}</Typography>
        <FormControl margin="normal" sx={{ mb: '8px' }}>
          <RadioGroup
            name="mode"
            value={state.mode.value}
            onChange={(event: any, newValue: any | null) => {
              handleOnChange('mode', newValue)
            }}
            sx={{ display: 'flex', gap: '15px', flexDirection: 'row' }}
          >
            {modeTypes.map((mode) => {
              return (
                <Box key={mode.value}>
                  <FormControlLabel
                    value={mode.index}
                    control={<Radio color="primary" />}
                    label={`${t(`bonusMode.${mode.value}`)}`}
                  />
                </Box>
              )
            })}
          </RadioGroup>
        </FormControl>
      </Box>
      {state.mode.value === BonusModeType.NON_STICKY && (
        <Box mt={3} mb={1} maxWidth={400}>
          <Typography fontWeight={700}>{t('wageringOrder')}</Typography>
          <FormControl margin="normal" sx={{ my: '8px' }}>
            <RadioGroup
              name="order"
              value={state.order.value}
              onChange={(event: any, newValue: any | null) => {
                handleOnChange('order', newValue)
              }}
              sx={{ display: 'flex', gap: '15px', flexDirection: 'row' }}
            >
              {wageringOrderOptions.map((order) => {
                return (
                  <Box key={order.value}>
                    <FormControlLabel
                      value={order.index}
                      control={<Radio color="primary" />}
                      label={t(`wageringOrderType.${order.value}`)}
                    />
                  </Box>
                )
              })}
            </RadioGroup>
          </FormControl>
        </Box>
      )}
      {state.order.value === BonusWageringOrderType.RM_FIRST &&
        state.mode.value === BonusModeType.NON_STICKY && (
          <Box maxWidth={770} mb={2} mt={1}>
            <Typography fontWeight={700} gutterBottom sx={{ mb: '15px' }}>
              {t('rmMinAmountCurrency')}
              <Tooltip
                sx={{ pl: 1, pt: 1 }}
                arrow
                title={`${t('rmMinAmountMessage')}`}
              >
                <Info />
              </Tooltip>
            </Typography>
            <CurrenciesTableComponent currencyHeadCells={currencyHeadCells}>
              {brand.currencies.map((currency) => (
                <TableRow key={`rmMinAmount_${currency}`}>
                  <TableCell>
                    {currency === CurrencyCodeEnum.USD
                      ? `${currency} ${t('labels.currencyDefault')}`
                      : currency}
                  </TableCell>
                  <TableCell sx={{ width: 400 || 'auto' }}>
                    <NumberFormatCustom
                      name={`rmMinAmount_${currency}`}
                      handleOnChange={handleOnChange}
                      state={state}
                      currency={currency}
                      prefix=""
                      style={{ padding: '16px 0' }}
                      fullwidth
                    />
                  </TableCell>
                </TableRow>
              ))}
            </CurrenciesTableComponent>
          </Box>
        )}
      <Divider light />
      <Box my={2} maxWidth={400}>
        <Typography fontWeight={700}>
          {t('rolloverCoefficcientType')}
        </Typography>
        <FormControl margin="normal" sx={{ my: '8px' }}>
          <RadioGroup
            name="rolloverCoefficcient"
            value={state.rolloverCoefficcient.value}
            onChange={(event: any, newValue: any | null) => {
              handleOnChange('rolloverCoefficcient', Number(newValue))
            }}
          >
            {rolloverTypes.map((rolloverType) => {
              return (
                <Box key={rolloverType.value}>
                  <FormControlLabel
                    value={rolloverType.index}
                    control={<Radio color="primary" />}
                    label={`${t(`rolloverAmountType.${rolloverType.value}`)}`}
                  />
                </Box>
              )
            })}
          </RadioGroup>
        </FormControl>
      </Box>
      <Box mb={2} maxWidth={400}>
        <TextField
          name="rollover"
          fullWidth
          margin="dense"
          variant="outlined"
          type="number"
          label={t('labels.rolloverCoefficient')}
          error={state.rollover.error.length > 0}
          helperText={sentenceCase(state.rollover.error, true)}
          value={state.rollover.value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleOnChange(event.target.name, event.target.value)
          }
        />
      </Box>
    </>
  )
}

export default RolloverSettingsComponent
